/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState, useEffect, useRef } from 'react';
import Card from '@salesforce/design-system-react/components/card';
import Button from '@salesforce/design-system-react/components/button';
import Combobox from '@salesforce/design-system-react/components/combobox';
import Tooltip from '@salesforce/design-system-react/components/tooltip';
import { useQuery } from '@apollo/react-hooks';
import { GET_USER_CONTACTS } from '../../../../../../graphql/getUserContacts';
import InputField from '../../../../../Shared/Input';
import { UsageSheetDetails } from '../../../UsageDetailsHeader';
import { formatTimestamp } from '../../../../../../util/utilityFunctions';
import '../../../index.scss';
import SignatureCanvas from './SignatureCanvas';
import { SIGNATURE_NAME } from '../../../../../../util/constants';

interface Props {
  usageDetails?: UsageSheetDetails;
  viewOnly: boolean;
  customerPrintedName: string;
  customerTitle: string;
  customerEmail: string;
  repPrintedName: string;
  repImgDataUrl: string;
  custImgDataUrl: string;
  repNameSign?: boolean;
  customerNameSign?: boolean;
  handleSaveSign: (url: string, signType: string) => void;
  onChangeCustName: (val: string) => void;
  onChangeCustTitle: (val: string) => void;
  onChangeCustEmail: (val: string) => void;
  onChangeRepName: (val: string) => void;
  onBlurField?: () => void;
  handleSave?: any;
}

interface Contact {
  id: string;
  name: string;
  email: string;
  customerTitle?: string;
  isfavourite?: boolean;
  contactRole: string;
  label?: string;
}

interface SalesRep {
  hospital?: string;
  id?: string;
  owned?: string;
  value?: string;
  label?: string;
}

interface ComboboxSelectData {
  selection: Contact[];
}

interface ComboboxChangeData {
  value: string;
}

const Signature: FC<Props> = ({
  usageDetails,
  viewOnly,
  customerPrintedName,
  customerTitle,
  customerEmail,
  repPrintedName,
  repImgDataUrl,
  custImgDataUrl,
  repNameSign,
  customerNameSign,
  handleSaveSign,
  onChangeCustName,
  onChangeCustTitle,
  onChangeCustEmail,
  onChangeRepName,
  onBlurField,
  handleSave,
}) => {
  const [signPadOpen, setSignPadOpen] = useState(false);
  const [signType, setSignType] = useState('');
  const [contactOptions, setContactOptions] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact[]>([]);
  const isDirty = useRef(false);

  const { data: getUserContacts } = useQuery(GET_USER_CONTACTS, {
    fetchPolicy: 'no-cache',
    variables: {
      userId: usageDetails?.salesRepId,
      accountId: usageDetails?.accountId,
    },
    skip: !usageDetails?.salesRepId || !usageDetails?.accountId,
  });

  // Format contacts for dropdown display
  useEffect(() => {
    if (getUserContacts?.getUserContacts) {
      const contacts: Contact[] = getUserContacts.getUserContacts.map((contact: Contact) => ({
        ...contact,
        label: `${contact.name}${contact.customerTitle ? ` - ${contact.customerTitle}` : ''}${contact.email ? ` <${contact.email}>` : ''}`,
      }));
      setContactOptions(contacts);
      
      // Pre-select if customerPrintedName matches a contact
      if (customerPrintedName) {
        const match = contacts.find((c: Contact) => c.name === customerPrintedName);
        if (match) {
          setSelectedContact([match]);
        }
      }
    }
  }, [getUserContacts, customerPrintedName]);

  const hasChanges = (): boolean => {
    if (customerPrintedName !== (usageDetails?.hospitalPrintedName || '')) return true;
    if (customerTitle !== (usageDetails?.customerTitle || '')) return true;
    if (customerEmail !== (usageDetails?.customerEmail || '')) return true;
    if (repPrintedName !== (usageDetails?.repPrintedName || '')) return true;
    return false;
  };

  const triggerBlurSave = (): void => {
    if (!isDirty.current || !onBlurField) return;
    isDirty.current = false;
    if (!hasChanges()) return;
    onBlurField();
  };

  const handleCustTitleChange = (val: string): void => {
    isDirty.current = true;
    onChangeCustTitle(val);
  };

  const handleCustEmailChange = (val: string): void => {
    isDirty.current = true;
    onChangeCustEmail(val);
  };

  const handleRepNameChange = (val: string): void => {
    isDirty.current = true;
    onChangeRepName(val);
  };

  const handleToggleModal = (): void => {
    setSignPadOpen(!signPadOpen);
  };
  const handleSignature = (type: string): void => {
    (document.activeElement as HTMLElement)?.blur();
    setSignType(type);
    setSignPadOpen(true);
  };
  const handleSaveSignature = (dataUrl: string): void => {
    handleSaveSign(dataUrl, signType);
    handleSave(true);
  };

  const handleClearSignature = (): void => {
    handleSaveSign('', signType);
    handleSave(true);
  };

  const handleContactSelect = (_event: unknown, data: ComboboxSelectData): void => {
    const selection = data.selection || [];
    setSelectedContact(selection);
    if (selection.length > 0) {
      const contact = selection[0];
      onChangeCustName(contact.name);
      onChangeCustTitle(contact.customerTitle || '');
      onChangeCustEmail(contact.email || '');
      if (onBlurField) onBlurField();
    }
  };

  const handleContactInputChange = (_event: unknown, data: ComboboxChangeData): void => {
    isDirty.current = true;
    const inputValue = data.value || '';
    onChangeCustName(inputValue);

    // Clear selection when manually typing
    setSelectedContact([]);
  };

  const handleContactBlur = (): void => {
    triggerBlurSave();
  };

  return (
    <Card id='Signature' heading='Signature'>
      <hr className='card-divider' />
      <div className='signature-container'>
        {customerNameSign && (
          <div>
            <Combobox
              classNameContainer="slds-m-bottom_small"
              disabled={viewOnly}
              labels={{
                label: 'Contact Printed Name',
                placeholder: 'Select or type contact name',
              }}
              required
              events={{
                onSelect: handleContactSelect,
                onChange: handleContactInputChange,
                onBlur: handleContactBlur,
              }}
              options={contactOptions}
              value={customerPrintedName}
            />
            <InputField
              disabled={viewOnly}
              label='Title'
              name='ContactTitle'
              placeholder='Contact Title'
              type='input'
              handleChangeText={handleCustTitleChange}
              value={customerTitle}
              onBlur={triggerBlurSave}
              required
            />
            <InputField
              disabled={viewOnly}
              label='Email'
              name='ContactEmail'
              placeholder='Contact Email'
              type='input'
              handleChangeText={handleCustEmailChange}
              value={customerEmail}
              onBlur={triggerBlurSave}
            />
            <InputField
              disabled={true}
              label='Signature Timestamp'
              name='SignatureTimestamp'
              placeholder='Not yet captured'
              type='input'
              handleChangeText={(): void => undefined}
              value={formatTimestamp(usageDetails?.signatureTimestamp)}
            />
            <div className='rep-column'>
              <div>
                <Button
                  label='Customer Signature'
                  onMouseDown={(): void => handleSignature(SIGNATURE_NAME.CUSTOMER)}
                  disabled={viewOnly || !customerPrintedName || !customerTitle}
                  className='yellow-btn sig-btn customer-sig-btn'
                  iconCategory='utility'
                  iconName='add'
                  iconPosition='left'
                />
                {(!customerPrintedName || !customerTitle) && (
                  <div className='signature-error-message'>
                    Please provide the contact's printed name and title to enable the signature field.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {repNameSign && (
          <div>
            <InputField
              disabled={viewOnly}
              label='Rep Printed Name'
              name='RepPrintedName'
              placeholder='Rep Printed Name'
              type='input'
              handleChangeText={handleRepNameChange}
              value={repPrintedName}
              onBlur={triggerBlurSave}
            />
            <div className='rep-column'>
              <Button
                label='Rep Signature'
                onMouseDown={(): void => handleSignature(SIGNATURE_NAME.REP)}
                disabled={viewOnly}
                className='yellow-btn sig-btn'
                iconCategory='utility'
                iconName='add'
                iconPosition='left'
              />
            </div>
          </div>
        )}
        <SignatureCanvas
          open={signPadOpen}
          handleToggleModal={handleToggleModal}
          handleSaveSignature={handleSaveSignature}
          existingSignature={signType === SIGNATURE_NAME.CUSTOMER ? custImgDataUrl : repImgDataUrl}
          onClearSignature={handleClearSignature}
        />
      </div>
    </Card>
  );
};

export default Signature;
